/** @jsxImportSource @emotion/react */

import { useTheme } from '@emotion/react';
import { forwardRef, MutableRefObject, useEffect, useRef } from 'react';

import { ProgressState } from '../App';
import { data, Progress } from '../Data/index';
import { ReactComponent as ConfettiGuy } from './confetti-guy.svg';

type ProgressBarProps = {
  onVisible: (element: HTMLDivElement) => void;
  progress: ProgressState;
};

export const SubmitPanel = forwardRef<HTMLDivElement, ProgressBarProps>(({ onVisible, progress }, ref) => {
  const theme = useTheme();
  const submitPanelRef = ref as MutableRefObject<HTMLDivElement>;

  const observer = useRef<IntersectionObserver>(
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5 && entry.target instanceof HTMLDivElement) {
            onVisible(entry.target);
          }
        });
      },
      {
        threshold: 0.5
      }
    )
  );

  useEffect(() => {
    const current = observer.current;
    current.disconnect();

    const submitPanel = submitPanelRef.current;

    if (submitPanel) {
      current.observe(submitPanel);
    }

    return () => {
      current.disconnect();
    };
  }, [submitPanelRef]);

  const buttonText = progress.status === Progress.Valid ? data.dictionary.pull_tarot_card : data.dictionary.form_error_button_text;
  const buttonType = progress.status === Progress.Valid ? 'pull' : 'answer';
  const panelText = progress.status === Progress.Valid ? data.dictionary.progress_complete : data.dictionary.form_error_text;

  const submitButton = (
    <button
      css={{
        boxSizing: 'border-box',
        background: theme.color.primary,
        border: '1px solid transparent',
        borderRadius: '2.5rem',
        color: theme.color.white,
        cursor: 'pointer',
        fontFamily: theme.fontFamily.body,
        fontSize: theme.fontSizes[2],
        fontWeight: theme.fontWeights.semibold,
        padding: '1rem 1.5rem',
        textTransform: 'uppercase',
        transition: 'background-color 100ms ease-in,border-color 100ms ease-in,color 100ms ease-in',

        '&:focus, &:hover': {
          background: theme.color.white,
          border: `1px solid ${theme.color.black}`,
          color: theme.color.primary
        },

        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },

        [`@media (min-width: 640px)`]: {
          fontSize: theme.fontSizes[4]
        }
      }}
      data-button-type={buttonType}
      type={'submit'}
    >
      {buttonText}
    </button>
  );

  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center'
      }}
      ref={submitPanelRef}
    >
      <div
        css={{
          marginBottom: '6rem'
        }}
      >
        {progress.status === Progress.Valid &&
          <div>
            <ConfettiGuy />
          </div>
        }
        <p
          css={{
            color: theme.color.slateDark,
            fontFamily: theme.fontFamily.heading,
            fontSize: theme.fontSizes[6],
            fontWeight: theme.fontWeights.semibold,
            marginTop: 0,
            marginBottom: '1.5rem',
            textAlign: 'center',

            [`@media (min-width: 640px)`]: {
              marginBottom: '2.5rem',
              fontSize: theme.fontSizes[10]
            }
          }}
        >{panelText}</p>
        <div>
          {submitButton}
        </div>
      </div>
    </div>
  );
});

SubmitPanel.displayName = 'SubmitPanel';
